import { useMutation, useQuery } from '@tanstack/react-query';
import { requestHandler } from 'lib/api';
import { errorHandler } from 'lib/functions/errors';
import { Dispatch } from 'redux';

import {
  CancelProcess,
  ClaimProcess,
  CreateForm,
  EmitWarrantyInvoiceBody,
  InvoicingReponse,
  MarkAsLostProcess,
  PaymentsResponse,
  UpdateForm,
} from './types';

export const getSGRWarrantyRequests = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/sgrWarrantyRequest?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessSGRWarrantyRequests = async (
  dispatch: Dispatch,
  businessIdentifier: string,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/sgrWarrantyRequest/${businessIdentifier}?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSGRWarrantyRequestFile = async (
  dispatch: Dispatch,
  warrantyRequestId: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/sgrWarrantyRequest/${warrantyRequestId}/file`,
    dispatch,
    responseType: 'arraybuffer',
  });
  if (response) {
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return {
      blob: blob,
      contentType: 'application/pdf',
    };
  }
};

export const getPublicSGRWarrantyRequestFile = async (
  dispatch: Dispatch,
  secretId: string,
  warrantyRequestId: number,
  clientIdentifier: string,
  principalIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `public/express/${secretId}/sgrWarrantyRequestFile/${warrantyRequestId}/${clientIdentifier}/${principalIdentifier}`,
    dispatch,
    responseType: 'arraybuffer',
  });
  if (response) {
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return {
      blob: blob,
      contentType: 'application/pdf',
    };
  }
};

export const getSGRWarrantyRequestPayments = (dispatch: Dispatch, id: number) =>
  useQuery({
    queryKey: ['sgrWarrantyRequest', 'payments', id],
    queryFn: async (): Promise<PaymentsResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/sgrWarrantyRequest/${id}/payments`,
        dispatch,
      });
      if (response) return response.data;
    },
    retry: 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
  });

export const getSGRWarrantyRequestInvoicing = (
  dispatch: Dispatch,
  id: number,
) =>
  useQuery({
    queryKey: ['sgrWarrantyRequestInvoicing', id],
    queryFn: async (): Promise<InvoicingReponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/sgrWarrantyRequest/${id}/invoicing`,
        dispatch,
      });
      if (response) return response.data;
    },
    retry: 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
  });

export const createSGRWarrantyRequest = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['create', 'sgrWarrantyRequest'],
    mutationFn: async (data: CreateForm) => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/sgrWarrantyRequest`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const emitWarrantyInvoice = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['emit', 'warrantyInvoice'],
    mutationFn: async (body: EmitWarrantyInvoiceBody) => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/sgrWarrantyRequest/invoice`,
        data: body,
        dispatch,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const updateSGRWarrantyRequest = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['update', 'sgrWarrantyRequest'],
    mutationFn: async (params: {
      warrantyRequestId: number;
      data: UpdateForm;
    }) => {
      const { warrantyRequestId, data } = params;
      const response = await requestHandler({
        method: 'put',
        url: `internal/sgrWarrantyRequest/${warrantyRequestId}`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const startClaimProcess = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['start', 'claimProcess'],
    mutationFn: async (params: { id: number; data: ClaimProcess }) => {
      const { id, data } = params;
      const response = await requestHandler({
        method: 'put',
        url: `internal/sgrWarrantyRequest/${id}/claim`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const markAsLostProcess = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['mark', 'asLost'],
    mutationFn: async (params: { id: number; data: MarkAsLostProcess }) => {
      const { id, data } = params;
      const response = await requestHandler({
        method: 'put',
        url: `internal/sgrWarrantyRequest/${id}/lost`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const cancelProcess = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['cancel', 'sgrWarrantyRequest'],
    mutationFn: async (params: { id: number; data: CancelProcess }) => {
      const { id, data } = params;
      const response = await requestHandler({
        method: 'put',
        url: `internal/sgrWarrantyRequest/${id}/cancel`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const deleteSGRWarrantyRequest = async (
  dispatch: Dispatch,
  warrantyRequestId: number,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/sgrWarrantyRequest/${warrantyRequestId}`,
    dispatch,
  });

  if (response) return response.data;
};
